import { useMemo, useRef } from "react";
import { OrthographicCamera } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { useGameStore } from "../../app/gameStore";
import { useSettingsStore } from "../../app/settingsStore";

/** True isometric angles: 45° yaw, arctan(1/sqrt(2)) pitch. */
const YAW = Math.PI / 4;
const PITCH = Math.atan(1 / Math.sqrt(2));
const DISTANCE = 14;
const ZOOM = 82;

const PARALLAX_STRENGTH = 0.4;
const PARALLAX_LERP = 0.08;

/** The widest stage the fixed zoom frames comfortably. */
const REFERENCE_COLS = 8;

/**
 * Fixed-angle orthographic isometric camera with gentle parallax-on-mouse,
 * per M1a. The base position/target never move; only a small pointer-driven
 * offset is lerped in, so the board reads as fixed-angle but alive.
 *
 * It centres on the board it is given rather than a constant, because stages
 * are different shapes, and pulls the zoom back for the wider ones so a
 * ten-column stage still fits the frame.
 */
export function CameraRig() {
  const cameraRef = useRef<THREE.OrthographicCamera>(null);
  const parallaxOffset = useRef(new THREE.Vector2(0, 0));
  const { pointer } = useThree();

  const cols = useGameStore((s) => s.snapshot.cols);
  const rows = useGameStore((s) => s.snapshot.rows);
  const reduceMotion = useSettingsStore((s) => s.reduceMotion);

  const lookAt = useMemo(
    () => new THREE.Vector3((cols - 1) / 2, 0, (rows - 1) / 2),
    [cols, rows],
  );
  const zoom = ZOOM * Math.min(1, REFERENCE_COLS / Math.max(cols, rows + 2));

  const basePosition = new THREE.Vector3(
    Math.sin(YAW) * Math.cos(PITCH) * DISTANCE,
    Math.sin(PITCH) * DISTANCE,
    Math.cos(YAW) * Math.cos(PITCH) * DISTANCE,
  ).add(lookAt);

  useFrame(() => {
    const camera = cameraRef.current;
    if (!camera) return;

    // Parallax is the whole reason the board feels like an object rather than
    // a picture, so reduced motion damps it hard rather than removing it —
    // and a camera that still answers the mouse a little keeps the depth cue
    // that tells you which tile you are over.
    const strength = reduceMotion ? PARALLAX_STRENGTH * 0.15 : PARALLAX_STRENGTH;
    const targetOffsetX = pointer.x * strength;
    const targetOffsetY = pointer.y * strength * 0.5;
    parallaxOffset.current.x += (targetOffsetX - parallaxOffset.current.x) * PARALLAX_LERP;
    parallaxOffset.current.y += (targetOffsetY - parallaxOffset.current.y) * PARALLAX_LERP;

    camera.position.set(
      basePosition.x + parallaxOffset.current.x,
      basePosition.y + parallaxOffset.current.y,
      basePosition.z,
    );
    camera.lookAt(lookAt);
  });

  return (
    <OrthographicCamera
      ref={cameraRef}
      makeDefault
      position={[basePosition.x, basePosition.y, basePosition.z]}
      zoom={zoom}
      near={0.1}
      far={100}
    />
  );
}
